import { isDisabled } from "@testing-library/user-event/dist/types/utils";
import React, { FC, useId, useState } from "react";

// Translation Package
import { UseFormRegisterReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

const mergeClasses = (classes) => {
  return Object.values(classes)
    .filter((c) => c !== null)
    .filter((c) => c !== false)
    .filter((c) => c !== undefined)
    .map((c) => c)
    .join(" ");
};

interface FormInputProps
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  register?: UseFormRegisterReturn;
  type?: string;
  name?: string;
  error?: { message?: string };
  errorMessage?: string;
  label?: string | React.ReactNode;
  id?: string;
  prependIcon?: React.ReactNode;
  appendIcon?: React.ReactNode;
  classSuffix?: string;
  floating?: boolean;
  forceLabelToFloat?: boolean;
  info?: string;
  showClearBtn?: boolean;
  onClear?: (event: React.MouseEvent<HTMLDivElement>) => void;
  beforeLabelContent?: string;
  labelClassSuffix?: string;
  required?: boolean;
  // For textarea
  rows?: number;
}
const FormInput: FC<FormInputProps> = ({
  register,
  type,
  name,
  error,
  errorMessage,
  label,
  id,
  prependIcon,
  appendIcon,
  placeholder,
  classSuffix,
  children,
  floating = true,
  forceLabelToFloat,
  info,
  showClearBtn,
  onClear,
  beforeLabelContent = "",
  labelClassSuffix = "",
  required,
  ...props
}) => {
  // Translation hook
  const { t, i18n } = useTranslation();
  const direction = props?.dir ?? i18n.dir();
  const randomId = useId();

  id = id || `input-${randomId}`;

  // Destructure register to avoid overriding props and handle events properly
  const {
    onChange: registerOnChange,
    onBlur: registerOnBlur,
    ref: registerRef,
    ...restRegister
  } = register || {};

  // Detect focus
  const [focused, setFocused] = useState(false);
  // const handleOnFocus = (e) => {
  //   setFocused(true);
  //   if (props?.onFocus) props.onFocus(e);
  //   else if (registerOnBlur) registerOnBlur(e);
  // };

  const handleOnBlur = (e) => {
    setFocused(false);
    if (props?.onBlur) props.onBlur(e);
  };

  const handleOnClear = (e) => {
    setFocused(false);
    if (onClear) onClear(e);
  };

  // Is password?
  const isPassword = type === "password";
  const [showPass, setShowPass] = useState(false);
  const togglePassword = (e) => {
    e.preventDefault();
    setShowPass(!showPass);
  };

  // Form group
  const formGroupClass = mergeClasses({
    default: "form-group floating-label",
    show: (forceLabelToFloat || focused) && "show-floating-label",
    suffix: classSuffix,
    error: !props?.disabled && error && "error",
  });

  // Input
  const inputClasses = {
    default: "form-control floating-label__text-input ",
    type: isPassword && "password-input",
  };
  const inputClass = mergeClasses(inputClasses);
  const inputProps = {
    type: isPassword ? (showPass ? "text" : type) : type,
    className: inputClass,
    name: name,
    id: id,
    placeholder: placeholder || " ",
    ...props,
  };
  const textarea = type === "textarea";

  return (
    <div className={formGroupClass}>
      {!floating && (
        <label htmlFor={id} className="form-control-label">
          {label} {`${required ? "*" : ""}`}
        </label>
      )}
      <div className="floating-label__input-area">
        {prependIcon && (
          <div className="floating-label__prepend-icon">{prependIcon}</div>
        )}
        {textarea ? (
          <textarea
            {...inputProps}
            {...restRegister}
            ref={registerRef}
            onFocus={(e) => {
              setFocused(true);
              if (props?.onFocus) props.onFocus(e);
              registerOnBlur?.(e);
            }}
            onChange={(e) => {
              if (props.onChange) props.onChange(e);
              registerOnChange?.(e);
            }}
            onBlur={(e) => {
              handleOnBlur(e);
              registerOnBlur?.(e);
            }}
            style={{ height: "200px" }}
            placeholder={!label || focused ? placeholder : ""}
          ></textarea>
        ) : (
          <input
            {...inputProps}
            value={props?.value}
            {...restRegister}
            onChange={(e) => {
              if (props.onChange) props.onChange(e);
              registerOnChange?.(e);
            }}
            // onFocus={handleOnFocus}
            onBlur={(e) => {
              handleOnBlur(e);
              registerOnBlur?.(e);
            }}
            ref={registerRef}
            // placeholder works only if input on focus
            placeholder={focused ? placeholder : " "}
          />
        )}

        {appendIcon && (
          <div className="floating-label__append-icon">{appendIcon}</div>
        )}

        {floating && (
          <label
            className={`floating-label__label
					${textarea ? "textarea-label" : ""}  ${labelClassSuffix}`}
            htmlFor={id}
            dir={direction}
            data-before-label-content={`${beforeLabelContent} `}
          >
            {label} {`${required ? "*" : ""}`}
          </label>
        )}

        {props?.value && showClearBtn && (
          <div
            className={`d-flex flex-row justify-content-center align-items-center position-absolute top-0 bottom-0 py-2 px-3 ${
              direction === "ltr" ? "end-0" : "start-0"
            }`}
            role="button"
            onClick={handleOnClear}
            style={{ transform: "translate(0.1px)" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={15}
              height={15}
              viewBox="0 0 352 512"
              fill="rgba(33, 37, 41, 0.7)"
            >
              <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z" />
            </svg>
          </div>
        )}
      </div>

      {isPassword && (
        <button
          className="show-password"
          onClick={togglePassword}
          type="button"
        >
          {showPass ? t("hide") : t("show")}
        </button>
      )}

      {children}

      {!props?.disabled && error && (
        <div className="error-text" hidden={error.message === ""}>
          {t(errorMessage) || t(error.message)}
        </div>
      )}

      {info && <div style={{ opacity: props.disabled ? 0.5 : 1 }} className="info-text">{info}</div>}
    </div>
  );
};

export default FormInput;
